import React from 'react';
import { Box, Typography, Grid, IconButton, Button, alpha } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import CustomerSelect from '../../features/invoices/components/CustomerSelect';

/**
 * DocumentHeaderSection Component
 * Displays company info (left) and customer info (right) side by side
 * Reusable across Delivery Notes, Sale Orders, and Invoices
 */
const DocumentHeaderSection = ({
  storeInfo,
  selectedCustomer,
  onCustomerChange,
  onCustomerClear,
  onAddCustomerClick,
  loadCustomerOptions,
}) => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={4} sx={{ mb: 4 }}>
      {/* LEFT: Company Information */}
      <Grid item xs={12} md={6}>
        <Typography 
          variant="overline" 
          fontWeight="600" 
          color="#667eea" 
          sx={{ fontSize: '0.75rem', letterSpacing: '1px', mb: 1, display: 'block' }}
        >
          {t("from") || "FROM"}
        </Typography>
        <Typography variant="h4" fontWeight="700" color="#2c3e50" sx={{ mb: 1.5 }}>
          {storeInfo?.name || t("company_name") || "Your Company Name"}
        </Typography>
        <Typography variant="body2" color="#555" sx={{ lineHeight: 2 }}>
          {storeInfo?.vat && <><strong>{t("vat_number")}:</strong> {storeInfo.vat}<br /></>}
          {storeInfo?.address && <>{storeInfo.address}<br /></>}
          {storeInfo?.postCode && storeInfo?.city && <>{storeInfo.postCode} {storeInfo.city}{storeInfo?.country ? ', ' + storeInfo.country : ''}</>}
        </Typography>
      </Grid>

      {/* RIGHT: Customer Information */}
      <Grid item xs={12} md={6}>
        {!selectedCustomer && (
          <Typography 
            variant="overline" 
            fontWeight="600" 
            color="#667eea" 
            sx={{ fontSize: '0.75rem', letterSpacing: '1px', mb: 1, display: 'block' }}
          >
            {t("ship_to") || "SHIP TO"}
          </Typography>
        )}
        
        {!selectedCustomer ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <CustomerSelect
                value={selectedCustomer}
                onChange={onCustomerChange}
                loadOptions={loadCustomerOptions}
              />
            </Box>
            {onAddCustomerClick && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAddCustomerClick}
                sx={{
                  borderRadius: '6px',
                  px: 2,
                  height: '38px',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    borderColor: '#764ba2',
                    bgcolor: alpha('#667eea', 0.05)
                  }
                }}
              >
                {t("add_new")}
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, justifyContent: 'flex-end' }}>
              <Box sx={{ 
                flex: '0 1 auto',
                minWidth: 0,
                maxWidth: '90%',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                borderLeft: '4px solid #667eea'
              }}>
                <Typography 
                  variant="overline" 
                  fontWeight="600" 
                  color="#667eea" 
                  sx={{ fontSize: '0.65rem', letterSpacing: '1px', mb: 0.5, display: 'block' }}
                >
                  {t("ship_to") || "SHIP TO"}
                </Typography>
                <Typography variant="h5" fontWeight="700" color="#2c3e50" sx={{ mb: 0.5 }}>
                  {selectedCustomer.Name || selectedCustomer.CompanyName}
                </Typography>
                <Typography variant="body2" color="#555" sx={{ lineHeight: 1.6 }}>
                  {(selectedCustomer.VATNumber || selectedCustomer.vat) && 
                    <><strong>{t("vat_number")}:</strong> {selectedCustomer.VATNumber || selectedCustomer.vat}<br /></>}
                  {selectedCustomer.Street && <>{selectedCustomer.Street}<br /></>}
                  {selectedCustomer.City && <>{selectedCustomer.PostCode || ''} {selectedCustomer.City}</>}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={onCustomerClear}
                sx={{ 
                  color: '#667eea',
                  mt: 0.5,
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: alpha('#667eea', 0.08)
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default DocumentHeaderSection;
